"use client";

import React from "react";
import { motion } from "framer-motion";

const faq = [
  {
    question: "Curated People, Not Just Passengers",
    answer: "Every group is intentionally designed – people who match your pace, mindset, and vibe. You come for the trip, but stay for the tribe.",
  },
  {
    question: "Mentor-Led Magic",
    answer: "Each outing is guided by an expert who’s been where you want to go – their insights and presence change the way you think.",
  },
  {
    question: "Real Outcomes, Not Just Reels",
    answer: "From content portfolios to career clarity, every trip leaves you with something tangible – not just pretty pictures.",
  },
  {
    question: "Un-Googleable Experiences",
    answer: "We craft moments you can’t DIY – sunset huddles, off-map cafés, and surprise detours designed to move you.",
  },
  {
    question: "Stays That Feel Like Soul Homes",
    answer: "Think creative villas and hilltop retreats. Aesthetic, comfortable, and specifically made for deep connection.",
  },
  {
    question: "All Sorted, No Stress",
    answer: "From curated pre-trip guides to group onboarding – we handle every detail so you can focus on the experience.",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { 
      delay: i * 0.08, 
      duration: 0.5, 
      ease: [0.215, 0.61, 0.355, 1] 
    },
  }),
};

const Intrigued = () => {
  return (
    <section className="py-10 lg:py-12 px-6 relative overflow-hidden ">
      <div className="absolute w-[600px] h-[600px] -top-40 -right-20 rounded-full blur-[120px] pointer-events-none " />
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <motion.h2
            className="font-bricolage font-extrabold text-[#0D0D0D] text-3xl md:text-5xl mb-4 tracking-tight"
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            This is Why <span className="text-[#FFAE00]">It&apos;s Not Just A Trip.</span>
          </motion.h2>
          <motion.p 
            className="font-bricolage font-medium text-gray-500 text-base md:text-lg max-w-xl mx-auto italic"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            &ldquo;Experiences designed to shift your perspective, not just your location.&rdquo;
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {faq.map((item, index) => (
            <motion.div
              key={item.question}
              custom={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariants}
              whileHover={{ y: -2 }}
              className="group relative p-[1.5px] rounded-[28px] overflow-hidden flex flex-col transition-all duration-500"
            >
              {/* Border animation active by default */}
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                style={{
                  background: "conic-gradient(from 0deg, transparent 50%, #FFAE00 80%, transparent 100%)",
                }}
                className="absolute inset-[-150%] opacity-40 group-hover:opacity-100 transition-opacity duration-500"
              />

              <div className="relative z-20 h-full w-full bg-white rounded-[27px] p-8 flex flex-col space-y-5
                border border-[#FFAE00]/10
                shadow-[0_20px_40px_rgba(0,0,0,0.08),inset_0_0_15px_rgba(255,174,0,0.1)]
                group-hover:shadow-[inset_0_4px_12px_rgba(0,0,0,0.07),inset_0_0_40px_rgba(255,174,0,0.2)] 
                group-hover:border-[#FFAE00]/40
                transition-all duration-500"
              >
                <div className="flex items-center justify-between">
                  <div className="h-1 w-16 bg-[#FFAE00] rounded-full group-hover:w-10 transition-all duration-500" />
                  <div className="h-9 w-9 rounded-full bg-[#F2F2F2] flex items-center justify-center shadow-inner">
                    <div className="h-2.5 w-2.5 rounded-full bg-[#FFAE00]" />
                  </div>
                </div>
                
                <h3 className="font-bricolage font-bold text-xl text-[#0D0D0D] leading-tight">
                  {item.question}
                </h3>
                
                <p className="font-jakarta text-gray-600 text-sm leading-relaxed antialiased">
                  {item.answer}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-16">
          <motion.button
            onClick={() => window.open("https://invite.outcomeouting.com/founders", "_blank")}
            className="bg-[#FFAE00] text-[#0D0D0D] px-10 py-4 rounded-full font-bricolage font-bold text-lg 
              shadow-[0_10px_25px_rgba(255,174,0,0.3),inset_0_-3px_0_rgba(0,0,0,0.1)]
              hover:shadow-[0_15px_35px_rgba(255,174,0,0.4),inset_0_-1px_0_rgba(0,0,0,0.1)]
              hover:translate-y-[-2px] active:translate-y-[1px] transition-all duration-200"
          >
            Request Invite →
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default Intrigued;